





















































































































































import { Component, Prop, Vue } from "vue-property-decorator";
import { ViewerSection, ViewerColumn, CellEditEvent } from "./DataViewerModels";
import { COLOR_TABLE_HEADER, COLOR_TABLE_HEADER_DARK, COLOR_PRIMARY } from "@/utils/colors";
import { themed } from "@/utils/Theme";

/**
 * A single collapsible section in the DataViewer.
 * Renders a searchable data table with optional inline editing.
 */
@Component
export default class DataViewerSection extends Vue {
  @Prop() section!: ViewerSection;
  @Prop({ default: false }) editable!: boolean;
  @Prop({ default: "" }) globalSearchQuery!: string;

  isExpanded = false;
  highlightedRowIndex: number | null = null;
  private highlightTimer: ReturnType<typeof setTimeout> | null = null;

  /** Currently editing cell identifier, or null if none. */
  private editingCellKey: string | null = null;
  /** Tracks the in-progress input value for the active cell. */
  private pendingEditValue: string | number | null = null;
  /** Sync'd search input for autocomplete fields. */
  autocompleteSearch: string | null = null;

  /** Section header background: light grey in light mode, dark elevated surface in dark mode. */
  get sectionHeaderColor(): string {
    return themed(COLOR_TABLE_HEADER, COLOR_TABLE_HEADER_DARK);
  }

  get primaryColor(): string {
    return COLOR_PRIMARY;
  }

  created() {
    this.isExpanded = this.section.expandedByDefault ?? false;
    this.buildSearchIndex();
  }

  get visibleColumns(): ViewerColumn[] {
    return this.section.columns.filter((col) => col.visible !== false);
  }

  get searchableKeys(): string[] {
    return this.visibleColumns
      .filter((col) => col.searchable !== false)
      .map((col) => col.key);
  }

  /**
   * Pre-computed lowercase search string per row.
   * Built once on creation; avoids repeated String + toLowerCase per cell per search.
   */
  private searchIndex: string[] = [];

  private buildSearchIndex() {
    const keys = this.section.columns
      .filter((col) => col.visible !== false && col.searchable !== false)
      .map((col) => col.key);
    this.searchIndex = this.section.rows.map((row) =>
      keys.map((k) => (row[k] != null ? String(row[k]) : "")).join("\t").toLowerCase()
    );
  }

  get activeSearchQuery(): string {
    return this.globalSearchQuery || "";
  }

  get filteredRows(): Record<string, string | number | null>[] {
    const query = this.activeSearchQuery.toLowerCase().trim();
    if (!query) {
      return this.section.rows;
    }
    const index = this.searchIndex;
    return this.section.rows.filter((_, i) => index[i].includes(query));
  }

  /** Rows enriched with _originalIndex for efficient template rendering. */
  get displayRows(): (Record<string, string | number | null> & { _originalIndex: number })[] {
    const query = this.activeSearchQuery.toLowerCase().trim();
    if (!query) {
      return this.section.rows.map((row, i) => ({ ...row, _originalIndex: i }));
    }
    const index = this.searchIndex;
    const result: (Record<string, string | number | null> & { _originalIndex: number })[] = [];
    for (let i = 0; i < this.section.rows.length; i++) {
      if (index[i].includes(query)) {
        result.push({ ...this.section.rows[i], _originalIndex: i });
      }
    }
    return result;
  }

  /** @deprecated Use displayRows with _originalIndex instead. Kept for scrollToAndHighlightRow. */
  getOriginalIndex(filteredIndex: number): number {
    return this.displayRows[filteredIndex]?._originalIndex ?? filteredIndex;
  }

  toggleExpand() {
    this.isExpanded = !this.isExpanded;
  }

  /**
   * Starts inline editing when an editable cell is clicked (single click).
   * Ignores clicks when not in edit mode, on non-editable columns, or on the
   * cell that is already being edited (so an in-progress value isn't reset).
   */
  onEditableCellClick(col: ViewerColumn, rowIndex: number) {
    if (!this.editable || col.editable !== true) return;
    if (this.isEditingCell(rowIndex, col.key)) return;
    this.startEditing(rowIndex, col.key);
  }

  /**
   * Resolves the picker items for a cell. Uses the column's per-row options
   * provider when present (allowed values depend on the row), otherwise the
   * static `pickerOptions`.
   */
  pickerItems(
    col: ViewerColumn,
    row: Record<string, string | number | null>
  ): { value: string | number | null; label: string }[] {
    return col.pickerOptionsForRow ? col.pickerOptionsForRow(row) : col.pickerOptions ?? [];
  }

  /** Activates inline editing for a specific cell. */
  startEditing(rowIndex: number, columnKey: string) {
    this.editingCellKey = `${rowIndex}:${columnKey}`;
    this.pendingEditValue = this.section.rows[rowIndex]?.[columnKey] ?? null;
  }

  /** Commits any pending edit value and deactivates inline editing. */
  stopEditing() {
    if (this.editingCellKey) {
      const separatorIndex = this.editingCellKey.indexOf(":");
      const rowIndex = parseInt(this.editingCellKey.substring(0, separatorIndex), 10);
      const colKey = this.editingCellKey.substring(separatorIndex + 1);
      this.onCellChange(rowIndex, colKey, this.pendingEditValue);
    }
    this.editingCellKey = null;
    this.pendingEditValue = null;
  }

  /** Updates the pending value as the user types. */
  onEditInput(value: string | number | null) {
    this.pendingEditValue = value;
  }

  /** Returns true if the given cell is currently being edited. */
  isEditingCell(rowIndex: number, columnKey: string): boolean {
    return this.editingCellKey === `${rowIndex}:${columnKey}`;
  }

  formatCell(value: string | number | null): string {
    if (value === null || value === undefined) return "—";
    return String(value);
  }

  getCellClass(col: ViewerColumn, row: Record<string, string | number | null>): Record<string, boolean> {
    return {
      "cell-editable": this.editable && col.editable === true,
      "cell-null": row[col.key] === null || row[col.key] === undefined,
    };
  }

  getCellStyle(col: ViewerColumn, row: Record<string, string | number | null>): Record<string, string> {
    if (!col.colorMap) return {};
    const value = row[col.key];
    if (value == null) return {};
    const color = col.colorMap[String(value)];
    if (!color) return {};
    return { color, fontWeight: "bold" };
  }

  onCellChange(rowIndex: number, columnKey: string, newValue: string | number | null) {
    const oldValue = this.section.rows[rowIndex]?.[columnKey] ?? null;
    if (oldValue === newValue) return;

    // Apply the change to the row data so the read-only display reflects it
    this.$set(this.section.rows[rowIndex], columnKey, newValue);

    const event: CellEditEvent = {
      sectionId: this.section.id,
      rowIndex,
      columnKey,
      oldValue,
      newValue,
    };
    this.$emit("cell-edit", event);
  }

  onCellClick(rowIndex: number, columnKey: string, value: string | number | null) {
    this.$emit("cell-click", {
      sectionId: this.section.id,
      rowIndex,
      columnKey,
      value,
    });
  }

  /** Scrolls to a row by its original index and highlights it briefly. */
  scrollToAndHighlightRow(rowIndex: number) {
    this.isExpanded = true;
    this.$nextTick(() => {
      const el = this.$el.querySelector(`tr[data-row-index="${rowIndex}"]`) as HTMLElement | null;
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      if (this.highlightTimer) clearTimeout(this.highlightTimer);
      this.highlightedRowIndex = rowIndex;
      this.highlightTimer = setTimeout(() => {
        this.highlightedRowIndex = null;
      }, 2000);
    });
  }
}
